import React, { useEffect, useState } from 'react';
import { membersApi } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Loader2, Users, CheckCircle2, MoreVertical, KeyRound, Pencil, Database, Trash2,
  Activity, ShieldAlert, Clock,
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_LABEL = {
  pending_contract: { label: 'Sin contrato', cls: 'status-pending' },
  pending_validation: { label: 'Pendiente validar', cls: 'status-pending' },
  effective: { label: 'Validada', cls: 'status-effective' },
};

export default function Companies() {
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [pwdReset, setPwdReset] = useState(null);
  const [newPwd, setNewPwd] = useState('');
  const [saving, setSaving] = useState(false);
  const [sessions, setSessions] = useState(null);          // datos de sesiones a mostrar
  const [sessionsMember, setSessionsMember] = useState(null); // empresa cuya ficha está abierta

  const load = () => {
    setLoading(true);
    membersApi.list().then(r => setItems(r.data)).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openSessions = async (m) => {
    setSessionsMember(m);
    setSessions(null);
    try {
      const r = await membersApi.sessions(m.id);
      setSessions(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las sesiones');
      setSessionsMember(null);
    }
  };

  const validate = async (id) => {
    try { await membersApi.validate(id); toast.success('Empresa validada'); load(); }
    catch { toast.error('Error al validar'); }
  };

  const openEdit = (m) => setEditing({ ...m });
  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        name: editing.name, organization: editing.organization, nif: editing.nif,
        email: editing.email, phone: editing.phone, address: editing.address,
        incorporation_status: editing.incorporation_status,
      };
      await membersApi.update(editing.id, payload);
      toast.success('Cambios guardados');
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally { setSaving(false); }
  };

  const openPwdReset = (m) => { setPwdReset(m); setNewPwd(''); };
  const doPwdReset = async () => {
    if (!newPwd || newPwd.length < 8) return toast.error('Mínimo 8 caracteres');
    setSaving(true);
    try {
      await membersApi.resetPassword(pwdReset.id, newPwd);
      toast.success(`Contraseña restablecida para ${pwdReset.email}`);
      const target = pwdReset;
      setPwdReset(null);
      // Abrir automáticamente el panel de sesiones para vigilar si la empresa vuelve a acceder
      openSessions(target);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al restablecer');
    } finally { setSaving(false); }
  };

  const doResetData = async (m) => {
    if (!window.confirm(`¿Borrar TODOS los datos históricos de "${m.organization}"?\n\nSe eliminarán envíos y datapoints. La cuenta y el contrato se mantendrán. Acción NO reversible.`)) return;
    try {
      const r = await membersApi.resetData(m.id);
      toast.success(`Datos borrados · ${r.data.deleted_datapoints} datapoints y ${r.data.deleted_submissions} envíos`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const doDelete = async (m) => {
    const confirm1 = window.confirm(`¿ELIMINAR completamente la empresa "${m.organization}" (${m.email})?\n\nSe borrará TODO: cuenta, contrato, envíos y datapoints históricos. Acción irreversible.`);
    if (!confirm1) return;
    const typed = window.prompt(`Confirmación: escriba el NIF "${m.nif}" para eliminar definitivamente`);
    if (typed !== m.nif) return toast.error('NIF no coincide, cancelado');
    try {
      const r = await membersApi.remove(m.id);
      toast.success(`Empresa eliminada · ${r.data.deleted_datapoints + r.data.deleted_submissions} registros borrados`);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al eliminar'); }
  };

  return (
    <div className="space-y-4 animate-fade-in-up">
      <Card className="aea-card">
        <CardHeader>
          <CardTitle className="flex items-center gap-2"><Users className="w-5 h-5 text-aea-blue" /> Empresas asociadas</CardTitle>
          <p className="text-sm text-slate-500">Alta, validación y gestión completa de participantes.</p>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-aea-blue" /></div> :
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="text-left p-3">Empresa</th>
                  <th className="text-left p-3">NIF/CIF</th>
                  <th className="text-left p-3">Contacto</th>
                  <th className="text-left p-3">Contrato</th>
                  <th className="text-left p-3">Estado</th>
                  <th className="text-right p-3">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 && <tr><td colSpan={6} className="p-8 text-center text-slate-500">Sin empresas registradas</td></tr>}
                {items.map((e) => {
                  const s = STATUS_LABEL[e.incorporation_status] || { label: e.incorporation_status, cls: 'status-draft' };
                  return (
                    <tr key={e.id} className="border-t" data-testid={`empresa-row-${e.id}`}>
                      <td className="p-3 font-medium">{e.organization}</td>
                      <td className="p-3 font-mono text-xs">{e.nif}</td>
                      <td className="p-3">
                        <div className="text-slate-700">{e.name}</div>
                        <div className="text-xs text-slate-400">{e.email}</div>
                      </td>
                      <td className="p-3">{e.contract_signed ? <span className="status-badge status-signed">Firmado</span> : <span className="status-badge status-pending">Pendiente</span>}</td>
                      <td className="p-3"><span className={`status-badge ${s.cls}`}>{s.label}</span></td>
                      <td className="p-3 text-right">
                        {isAdmin && (
                          <div className="flex items-center justify-end gap-2">
                            {e.incorporation_status === 'pending_validation' && (
                              <Button size="sm" onClick={() => validate(e.id)} className="bg-aea-blue hover:bg-aea-blue-dk" data-testid={`btn-validate-${e.id}`}>
                                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Validar
                              </Button>
                            )}
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button size="sm" variant="outline" data-testid={`menu-empresa-${e.id}`}>
                                  <MoreVertical className="w-3.5 h-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuItem onClick={() => openSessions(e)} data-testid={`btn-sessions-${e.id}`}>
                                  <Activity className="w-4 h-4 mr-2" /> Ver sesiones
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEdit(e)} data-testid={`btn-edit-${e.id}`}>
                                  <Pencil className="w-4 h-4 mr-2" /> Editar datos
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openPwdReset(e)} data-testid={`btn-resetpwd-${e.id}`}>
                                  <KeyRound className="w-4 h-4 mr-2" /> Restablecer contraseña
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem onClick={() => doResetData(e)} className="text-amber-700 focus:text-amber-700"
                                                  data-testid={`btn-resetdata-${e.id}`}>
                                  <Database className="w-4 h-4 mr-2" /> Borrar datos históricos
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => doDelete(e)} className="text-red-600 focus:text-red-700"
                                                  data-testid={`btn-delete-${e.id}`}>
                                  <Trash2 className="w-4 h-4 mr-2" /> Eliminar empresa
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          }
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Editar empresa · {editing?.organization}</DialogTitle></DialogHeader>
          {editing && (
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Label>Razón social</Label><Input value={editing.organization || ''} onChange={e => setEditing({...editing, organization: e.target.value})} data-testid="edit-org" /></div>
              <div><Label>NIF/CIF</Label><Input value={editing.nif || ''} onChange={e => setEditing({...editing, nif: e.target.value})} data-testid="edit-nif" /></div>
              <div><Label>Estado</Label>
                <Select value={editing.incorporation_status} onValueChange={(v) => setEditing({...editing, incorporation_status: v})}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="pending_contract">Sin contrato</SelectItem>
                    <SelectItem value="pending_validation">Pendiente validar</SelectItem>
                    <SelectItem value="effective">Validada</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Contacto</Label><Input value={editing.name || ''} onChange={e => setEditing({...editing, name: e.target.value})} data-testid="edit-name" /></div>
              <div><Label>Email</Label><Input value={editing.email || ''} onChange={e => setEditing({...editing, email: e.target.value})} data-testid="edit-email" /></div>
              <div><Label>Teléfono</Label><Input value={editing.phone || ''} onChange={e => setEditing({...editing, phone: e.target.value})} data-testid="edit-phone" /></div>
              <div><Label>Dirección</Label><Input value={editing.address || ''} onChange={e => setEditing({...editing, address: e.target.value})} data-testid="edit-address" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancelar</Button>
            <Button className="bg-aea-blue hover:bg-aea-blue-dk" onClick={saveEdit} disabled={saving} data-testid="edit-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Guardar cambios'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Password reset dialog */}
      <Dialog open={!!pwdReset} onOpenChange={(o) => !o && setPwdReset(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Restablecer contraseña</DialogTitle></DialogHeader>
          {pwdReset && (
            <div className="space-y-3">
              <div className="p-3 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800">
                Está a punto de restablecer la contraseña de <b>{pwdReset.organization}</b> ({pwdReset.email}).
                Comunique la nueva contraseña por un canal seguro. La empresa deberá cambiarla en su primer acceso.
              </div>
              <div>
                <Label>Nueva contraseña (mín. 8 caracteres)</Label>
                <Input type="text" value={newPwd} onChange={e => setNewPwd(e.target.value)} data-testid="reset-pwd-input"
                       placeholder="Ej: TempAEA2026!" />
              </div>
              <div className="text-xs text-slate-500 flex items-center gap-1">
                <Activity className="w-3.5 h-3.5" />
                Tras restablecer se abrirá automáticamente el panel de sesiones para verificar si la empresa vuelve a entrar.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPwdReset(null)}>Cancelar</Button>
            <Button className="bg-aea-blue hover:bg-aea-blue-dk" onClick={doPwdReset} disabled={saving} data-testid="reset-pwd-confirm">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Restablecer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sessions dialog */}
      <Dialog open={!!sessionsMember} onOpenChange={(o) => !o && setSessionsMember(null)}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-aea-blue" />
              Sesiones de {sessionsMember?.organization}
            </DialogTitle>
          </DialogHeader>
          {!sessions ? (
            <div className="flex justify-center py-10"><Loader2 className="animate-spin text-aea-blue" /></div>
          ) : (
            <div className="space-y-4">
              {/* Alerta reset reciente */}
              {sessions.summary.last_password_reset_at && (
                <div className={`p-3 rounded border text-sm ${
                  sessions.summary.logged_in_since_last_reset
                    ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                    : 'bg-amber-50 border-amber-200 text-amber-800'
                }`}>
                  <div className="flex items-start gap-2">
                    <ShieldAlert className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">
                        Último reset de contraseña: {new Date(sessions.summary.last_password_reset_at).toLocaleString('es-ES')}
                      </div>
                      <div className="text-xs mt-1">
                        Ejecutado por <b>{sessions.summary.last_password_reset_by}</b>.{' '}
                        {sessions.summary.logged_in_since_last_reset
                          ? '✅ La empresa ha vuelto a iniciar sesión tras el reset.'
                          : '⚠️ La empresa NO ha vuelto a iniciar sesión desde entonces.'}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* KPIs de resumen */}
              <div className="grid grid-cols-3 gap-3">
                <div className="border rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Último acceso</div>
                  <div className="text-sm font-semibold mt-1">
                    {sessions.summary.last_login_at
                      ? new Date(sessions.summary.last_login_at).toLocaleString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
                      : '—'}
                  </div>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">{sessions.summary.last_login_ip || ''}</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Últimas 48h</div>
                  <div className="text-2xl font-outfit font-semibold mt-1 text-aea-blue">{sessions.summary.logins_last_48h}</div>
                  <div className="text-xs text-slate-400">accesos</div>
                </div>
                <div className="border rounded-lg p-3">
                  <div className="text-xs text-slate-500 uppercase tracking-wider">Historial</div>
                  <div className="text-2xl font-outfit font-semibold mt-1">{sessions.recent_logins.length}</div>
                  <div className="text-xs text-slate-400">últimos logins</div>
                </div>
              </div>

              {/* Tabla de logins */}
              <div>
                <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                  <Clock className="w-3.5 h-3.5" /> Historial de accesos
                </div>
                <div className="max-h-64 overflow-y-auto border rounded-lg">
                  <table className="w-full text-xs">
                    <thead className="bg-slate-50 sticky top-0">
                      <tr>
                        <th className="p-2 text-left">Fecha y hora</th>
                        <th className="p-2 text-left">IP</th>
                        <th className="p-2 text-right">Antigüedad</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessions.recent_logins.length === 0 && (
                        <tr><td colSpan={3} className="p-4 text-center text-slate-500">Sin accesos registrados</td></tr>
                      )}
                      {sessions.recent_logins.map((l, i) => {
                        const d = new Date(l.timestamp);
                        const hoursAgo = Math.floor((Date.now() - d.getTime()) / 3600000);
                        return (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono">{d.toLocaleString('es-ES')}</td>
                            <td className="p-2 font-mono text-slate-600">{l.ip_address}</td>
                            <td className="p-2 text-right text-slate-500">
                              {hoursAgo < 1 ? 'hace < 1 h'
                                : hoursAgo < 48 ? `hace ${hoursAgo} h`
                                : `hace ${Math.floor(hoursAgo / 24)} d`}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Eventos de seguridad */}
              {sessions.security_events.length > 0 && (
                <div>
                  <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider mb-2">Eventos de seguridad</div>
                  <div className="max-h-40 overflow-y-auto border rounded-lg">
                    <table className="w-full text-xs">
                      <tbody>
                        {sessions.security_events.map((e, i) => (
                          <tr key={i} className="border-t">
                            <td className="p-2 font-mono text-slate-500">{new Date(e.timestamp).toLocaleString('es-ES')}</td>
                            <td className="p-2"><code className="bg-slate-100 px-1.5 py-0.5 rounded">{e.action}</code></td>
                            <td className="p-2 text-slate-600">{e.performed_by}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSessionsMember(null)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
